import { useEffect, useRef, useState } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls, Html } from "@react-three/drei";
import { FadeIn, SectionTag } from "@/components/Reveal";

const LAYERS = [
  {
    name: "Wearing Course",
    dim: "40–50 mm",
    material: "Bituminous Concrete — VG-40 / PMB",
    desc: "The top skin that fights traffic, heat and rain. On heavy corridors it is built with stiffer binders like VG-40 or Polymer Modified Bitumen so the surface never ruts or bleeds.",
    product: "Bitumen VG-40 · PMB",
    color: "#1c1c1c",
    h: 0.32,
  },
  {
    name: "Tack Coat",
    dim: "0.2–0.4 kg/m² spray",
    material: "Bitumen Emulsion SS-1",
    desc: "A thin amber spray of SS-1 emulsion that welds the fresh bituminous layer to the one below. Skip it and the layers slip against each other under braking.",
    product: "Emulsion SS-1",
    color: "#FF5722",
    h: 0.07,
  },
  {
    name: "Binder Course",
    dim: "50–100 mm",
    material: "Dense Bituminous Macadam — VG-30",
    desc: "The structural muscle of the pavement. Dense Bituminous Macadam with VG-30 carries the axle loads and distributes them to the layers below.",
    product: "Bitumen VG-30",
    color: "#262626",
    h: 0.5,
  },
  {
    name: "Prime Coat",
    dim: "0.7–1.5 kg/m² spray",
    material: "Bitumen Emulsion MS-1 / RS-1",
    desc: "Emulsion sprayed over the granular base — it penetrates, seals and primes the surface so the bituminous courses above bond permanently.",
    product: "Emulsion MS-1 · RS-1",
    color: "#E64A19",
    h: 0.07,
  },
  {
    name: "Granular Base",
    dim: "200–250 mm",
    material: "Wet Mix Macadam (WMM)",
    desc: "Well-graded crushed aggregates that spread the load. When potholes appear here, our emulsion-based Cold Mix patches them instantly — no heating needed.",
    product: "Cold Mix for repairs",
    color: "#3a3a3a",
    h: 0.55,
  },
  {
    name: "Subbase",
    dim: "150–300 mm",
    material: "Granular Subbase (GSB)",
    desc: "The drainage and support layer. It keeps water away from the structure so the pavement survives monsoon after monsoon.",
    product: null,
    color: "#4a423a",
    h: 0.55,
  },
  {
    name: "Subgrade",
    dim: "300–500 mm compacted",
    material: "Compacted Natural Soil",
    desc: "The prepared foundation of natural soil. Every layer above is only ever as strong as this compaction.",
    product: null,
    color: "#5a4632",
    h: 0.8,
  },
];

const TOTAL_H = LAYERS.reduce((s, l) => s + l.h, 0);
const STACK_Y = LAYERS.map((l, i) => LAYERS.slice(i + 1).reduce((s, x) => s + x.h, 0) + l.h / 2 - TOTAL_H / 2);
const EXPLODE_GAP = 0.42;

function LayerBox({ layer, index, selected, exploded, onSelect }) {
  const ref = useRef();
  const isActive = selected === index;
  useFrame((_, dt) => {
    if (!ref.current) return;
    const explodeOffset = exploded ? (LAYERS.length - 1 - index) * EXPLODE_GAP : 0;
    const lift = isActive ? 0.25 : 0;
    const targetY = STACK_Y[index] + explodeOffset + lift;
    ref.current.position.y += (targetY - ref.current.position.y) * Math.min(1, dt * 4.5);
    const targetScale = isActive ? 1.04 : 1;
    ref.current.scale.x += (targetScale - ref.current.scale.x) * Math.min(1, dt * 6);
    ref.current.scale.z += (targetScale - ref.current.scale.z) * Math.min(1, dt * 6);
  });
  return (
    <mesh
      ref={ref}
      position={[0, STACK_Y[index], 0]}
      onClick={(e) => {
        e.stopPropagation();
        onSelect(index);
      }}
      onPointerOver={(e) => {
        e.stopPropagation();
        onSelect(index);
        document.body.style.cursor = "pointer";
      }}
      onPointerOut={() => {
        document.body.style.cursor = "auto";
      }}
    >
      <boxGeometry args={[6, layer.h, 3.2]} />
      <meshStandardMaterial
        color={layer.color}
        emissive={isActive ? "#FF5722" : "#000000"}
        emissiveIntensity={isActive ? 0.5 : 0}
        roughness={0.85}
        metalness={0.05}
      />
      <Html
        position={[3.25, 0, 0]}
        distanceFactor={11}
        occlude
        zIndexRange={[30, 0]}
        style={{ pointerEvents: "none" }}
      >
        <div
          className={`flex items-center gap-2 whitespace-nowrap border-l-2 bg-black/70 py-1 pl-2 pr-3 font-mono backdrop-blur-sm transition-colors ${
            isActive ? "border-brand" : "border-white/25"
          }`}
          data-testid={`road-label-${index}`}
        >
          <span className={`text-[10px] uppercase tracking-[0.15em] ${isActive ? "text-white" : "text-neutral-300"}`}>
            {layer.name}
          </span>
          <span className="text-[10px] font-medium text-brand">{layer.dim}</span>
        </div>
      </Html>
    </mesh>
  );
}

function Scene({ selected, setSelected, exploded }) {
  return (
    <>
      <ambientLight intensity={0.55} />
      <directionalLight position={[6, 10, 4]} intensity={1.5} />
      <pointLight position={[-6, 4, -4]} intensity={35} distance={25} color="#FF5722" />
      <group position={[0, -1.75, 0]}>
        {LAYERS.map((l, i) => (
          <LayerBox key={l.name} layer={l} index={i} selected={selected} exploded={exploded} onSelect={setSelected} />
        ))}
      </group>
      <gridHelper args={[18, 18, "#2a2a2a", "#151515"]} position={[0, -2.6, 0]} />
      <OrbitControls
        enableZoom={false}
        enablePan={false}
        autoRotate
        autoRotateSpeed={0.9}
        minPolarAngle={Math.PI / 4}
        maxPolarAngle={Math.PI / 2.1}
      />
    </>
  );
}

export default function RoadAnatomy() {
  const [selected, setSelected] = useState(0);
  const [exploded, setExploded] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setTimeout(() => setExploded(true), 500);
          obs.disconnect();
        }
      },
      { threshold: 0.25 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const active = LAYERS[selected];

  return (
    <section className="border-b border-white/10 bg-[#0C0C0C] px-6 py-24 md:px-12 md:py-32 lg:px-20" data-testid="road-anatomy" ref={wrapRef}>
      <SectionTag index="02" label="Road Anatomy" />
      <div className="mt-6 grid gap-6 lg:grid-cols-12 lg:items-end">
        <h2 className="font-display text-4xl font-semibold uppercase leading-[1.08] tracking-normal sm:text-5xl sm:leading-[1.08] lg:col-span-7">
          Every layer. Engineered.
        </h2>
        <p className="max-w-md text-sm leading-relaxed text-neutral-400 lg:col-span-5">
          A modern highway is a stack of engineered layers — and bitumen binds the critical ones. Drag to rotate,
          hover or tap any layer to see what it does and which Catalyst product builds it.
        </p>
      </div>

      <div className="mt-14 grid gap-10 lg:grid-cols-12">
        <FadeIn className="lg:col-span-7">
          <div className="h-[420px] w-full border border-white/10 bg-[#0A0A0A] md:h-[540px]" data-testid="road-canvas">
            <Canvas dpr={[1, 1.5]} camera={{ position: [7.5, 5.2, 8.5], fov: 40 }}>
              <Scene selected={selected} setSelected={setSelected} exploded={exploded} />
            </Canvas>
          </div>
          <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-600">
            Flexible pavement per IRC:37 — drag to orbit
          </p>
        </FadeIn>

        <FadeIn delay={0.15} className="lg:col-span-5">
          <div className="border border-white/10">
            {LAYERS.map((l, i) => (
              <button
                key={l.name}
                onMouseEnter={() => setSelected(i)}
                onClick={() => setSelected(i)}
                data-testid={`road-layer-${i}`}
                className={`flex w-full items-center gap-4 border-b border-white/10 px-5 py-3.5 text-left transition-colors duration-300 last:border-b-0 ${
                  selected === i ? "bg-brand/10" : "hover:bg-white/[0.03]"
                }`}
              >
                <span className={`font-mono text-xs ${selected === i ? "text-brand" : "text-neutral-600"}`}>
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className={`font-display text-sm uppercase tracking-wide ${selected === i ? "text-white" : "text-neutral-400"}`}>
                  {l.name}
                </span>
                <span className="ml-auto h-2.5 w-2.5 rounded-sm" style={{ background: l.color }} />
              </button>
            ))}
          </div>
          <div className="mt-6 border border-white/10 bg-[#0A0A0A] p-6" data-testid="road-layer-info">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand">{active.material}</p>
            <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
              Typical section — {active.dim}
            </p>
            <p className="mt-3 text-sm leading-relaxed text-neutral-300">{active.desc}</p>
            {active.product && (
              <p className="mt-4 inline-block border border-brand/50 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-brand">
                Catalyst supplies — {active.product}
              </p>
            )}
          </div>
        </FadeIn>
      </div>
    </section>
  );
}
